<template>
	<div
		class="event-conversion-offers"
		v-if="resourcesForEventiumOffers?.length || mutatedFreePack"
		:data-event-type="attributes.type"
	>
		<div class="event-conversion-offers-info" v-if="conversionBonus > 1">
			{{
				translate("event.conversion_rate_boosted").replace(
					"{percent}",
					`${((conversionBonus - 1) * 100).toFixed(0)}%`,
				)
			}}
		</div>
		<div
			v-for="(offer, index) in resourcesForEventiumOffers"
			:ref="Object.keys(offer.value as Record<string, number>)[0]"
			:key="index"
			class="ref-section-wrapper"
		>
			<InGameShopItem
				:key="index"
				:contentData="offer"
				@buyPackage="() => buyInGamePackage(offer)"
			/>
			<div class="event-conversion-offers__toast">
				<ToastMessage
					:key="toastRenderKey"
					width="auto"
					:translatedText="toastMessage"
					:showMessage="
						showToast && toastOfferKey === getOfferKey(offer)
					"
					:hasAnimation="true"
				/>
			</div>
		</div>
		<div class="ref-section-wrapper-free" v-if="mutatedFreePack">
			<InGameShopItem
				:contentData="mutatedFreePack"
				:showNotification="freeOfferAvailable"
				@buyPackage="() => claimFreePack(mutatedFreePack)"
			/>
		</div>
	</div>
</template>

<script setup lang="ts">
import ToastMessage from "@/components/ToastMessage.vue";
import InGameShopItem from "@/components/shop/cards/InGameShopItem.vue";
import { useEventStore } from "@/event";
import type { ResourceType } from "@/game/entities/config/resources";
import useEmitter, { EmitterEvents } from "@/hooks/useEmitter";
import useGameTicker from "@/hooks/useGameTicker";
import useResourcesStore from "@/store/useResourcesStore";
import useUserStore from "@/store/useUserStore";
import type { ConversionOffer } from "@/types/shop";
import { OfferType } from "@/types/ui";
import { translate } from "@/utils/translate";
import { storeToRefs } from "pinia";
import { computed, markRaw, onUnmounted, ref, watch } from "vue";

// Provider importy
import type { Offer } from "@/game/config/gameCurrencyOffers";
import { CalculationType, OfferProvider } from "@/services/OfferProvider";
import { PaymentProvider } from "@/services/PaymentProvider";
import { OfferStatus } from "@/types/shop";

const emitter = markRaw(useEmitter());
const ticker = markRaw(useGameTicker());

const resourcesStore = useResourcesStore();
const { increase } = resourcesStore;

const userStore = useUserStore();
const eventStore = useEventStore();
const { attributes } = storeToRefs(eventStore);

const resourcesForEventiumOffers = OfferProvider.eventiumOffers;
const mutatedFreePack = OfferProvider.eventiumFreePack;

const freeOfferAvailable = computed(
	(): boolean => OfferProvider.freeEventOfferAvailable,
);

const conversionBonus = computed((): number => OfferProvider.conversionBonus);
const showToast = ref<boolean>(false);
const toastMessage = ref<string>("");
const toastOfferKey = ref<string | null>(null);
const toastRenderKey = ref<number>(0);
let toastTimeout: ReturnType<typeof setTimeout> | null = null;

watch(
	conversionBonus,
	(): void => {
		OfferProvider.calculate(CalculationType.EVENTIUM_OFFERS);
	},
	{ immediate: true },
);

const getOfferKey = (offer: ConversionOffer): string =>
	`${offer.type}_${offer.id}`;

const showNotEnoughResourcesToast = (offer: ConversionOffer): void => {
	toastMessage.value = translate("event.not_enough_resources");
	toastOfferKey.value = getOfferKey(offer);
	toastRenderKey.value += 1;
	showToast.value = true;

	if (toastTimeout) {
		clearTimeout(toastTimeout);
	}

	toastTimeout = setTimeout((): void => {
		showToast.value = false;
		toastOfferKey.value = null;
		toastTimeout = null;
	}, 1500);
};

onUnmounted((): void => {
	if (toastTimeout) {
		clearTimeout(toastTimeout);
	}
});

const claimFreePack = async (offer: ConversionOffer | null): Promise<void> => {
	if (!offer || !freeOfferAvailable.value) return;

	userStore.setIsDailyFreeEventPackClaimed();
	PaymentProvider.emitGenericClick();

	if (offer.value) {
		for (const [key, value] of Object.entries(
			offer.value as Partial<Record<ResourceType, number>>,
		) as Array<[ResourceType, number]>) {
			increase(key, value, "event shop free");
		}
	}

	emitter.emit(EmitterEvents.ShopItemPurchased, {
		offerId: offer.id,
		offerType: offer.type,
	});

	await PaymentProvider.handleInAppPayment();
	OfferProvider.calculate(CalculationType.EVENTIUM_OFFERS);
};

const buyInGamePackage = async (offer: ConversionOffer): Promise<void> => {
	PaymentProvider.emitGenericClick();

	if (offer.timeLimit) return;
	if (offer.type === OfferType.ACTION_FOR_GEMS && !offer.condition?.())
		return;

	ticker.stop();

	// Delegate resource logic to Provider
	// Cast as Offer (Offer/ConversionOffer share price/value)
	const processStatus = await PaymentProvider.handleResourcesUpdate(
		offer as unknown as Offer,
	);

	if (processStatus === OfferStatus.SUCCESS) {
		userStore.setMarketplaceTimestamps(
			`${offer.type}_${offer.id}`,
			Date.now(),
		);
		offer.callback?.();

		emitter.emit(EmitterEvents.ShopItemPurchased, {
			offerId: offer.id,
			offerType: offer.type,
		});

		await PaymentProvider.handleInAppPayment();
		OfferProvider.calculate(CalculationType.EVENTIUM_OFFERS);
	} else if (
		processStatus === OfferStatus.NOT_ENOUGH_RESOURCES &&
		offer.price
	) {
		PaymentProvider.resumeGame();
		showNotEnoughResourcesToast(offer);
	} else {
		PaymentProvider.resumeGame();
	}
};
</script>

<style lang="scss" scoped>
.event-conversion-offers {
	display: flex;
	flex-direction: column;
	gap: 1rem;
	width: 100%;

	.ref-section-wrapper {
		position: relative;
	}

	&__toast {
		position: absolute;
		top: 60%;
		left: 60%;
		transform: translate(-50%, -50%);
		z-index: 2;
		pointer-events: none;
	}

	:deep(.regular-shop-item) {
		border-image-source: linear-gradient(
			0deg,
			rgba(192, 139, 38, 1) 0%,
			rgba(206, 174, 86, 1) 100%
		);
		background: linear-gradient(
			0deg,
			rgba(168, 108, 14, 1) 0%,
			rgba(238, 178, 74, 1) 100%
		);
		.regular-shop-item__footer[data-v-fe39be36]
			.box-header-bg
			.solid-bg-polygon {
			fill: #0d6c27 !important;
		}
		.regular-shop-item__icon-wrapper {
			@include background(
				url($path-events + "christmas/backgrounds/reward-box-event.avif"),
				contain,
				center,
				no-repeat
			);
		}
	}
	.ref-section-wrapper-free {
		:deep(.regular-shop-item) {
			background: linear-gradient(
				0deg,
				rgba(74, 82, 9, 1) 0%,
				rgba(151, 166, 30, 1) 100%
			);
			.regular-shop-item__footer[data-v-fe39be36]
				.box-header-bg
				.solid-bg-polygon {
				fill: #303700 !important;
			}
		}
	}
	&-info {
		color: #fff3dd;
		font-size: 0.9rem;
		text-align: center;
	}
	&[data-event-type="firefest"] {
		:deep(.regular-shop-item) {
			.regular-shop-item__icon-wrapper {
				@include background(
					url($path-events + "firefest/backgrounds/reward-box-event.avif"),
					contain,
					center,
					no-repeat
				);
			}
		}
	}
	&[data-event-type="halloween"] {
		:deep(.regular-shop-item) {
			.regular-shop-item__icon-wrapper {
				@include background(
					url($path-events + "halloween/backgrounds/reward-box-event.avif"),
					contain,
					center,
					no-repeat
				);
			}
		}
	}
}
</style>
